import { useEffect } from 'react'
import { useRouter } from 'next/router'

import config from '@/utils/apiHelpers/configHelper'
import { initLocalizations } from '@/utils/apiHelpers/i18nApiHelper'
import pubhub from '@/utils/apiHelpers/pubhubData'
import { proxyInstance } from '@/hooks/useApi'

const isProd = process.env.NODE_ENV === 'production'
const basePath = process.env.NEXT_PUBLIC_BASE_PATH || ''

const cleanPath = p => {
  if (!p) return '/'
  const base = p.split('?')[0].split('#')[0]
  const noTrailing = base.replace(/\/+$/, '')
  return noTrailing || '/'
}

let swRegistering = null
let didBootstrap = false

const waitForSWControl = async () => {
  await navigator.serviceWorker.ready
  if (!navigator.serviceWorker.controller) {
    await new Promise(resolve => {
      const onCtl = () => {
        navigator.serviceWorker.removeEventListener('controllerchange', onCtl)
        resolve()
      }
      navigator.serviceWorker.addEventListener('controllerchange', onCtl)
    })
  }
}

const bootstrapData = async () => {
  if (didBootstrap) return
  didBootstrap = true

  await Promise.all([
    proxyInstance.get('clientApi'),
    proxyInstance.get('version'),
    config.getConfigs(),
    initLocalizations(),
    pubhub.getProducts(),
    pubhub.getLocales(),
    pubhub.getMetadataTags()
  ])
}

const unregisterAllSW = async () => {
  try {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return
    const regs = await navigator.serviceWorker.getRegistrations()
    await Promise.all(regs.map(r => r.unregister()))
  } catch {}
}

const registerSW = async () => {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return

  const swUrl = `${basePath}/sw.js`
  const scope = `${basePath}/`

  if (!swRegistering) {
    if (window.serwist && typeof window.serwist.register === 'function') {
      swRegistering = window.serwist.register({ scope })
    } else {
      swRegistering = navigator.serviceWorker.register(swUrl, { scope })
    }
  }

  await swRegistering
  await waitForSWControl()
  if (navigator.onLine) await bootstrapData()
}

export const useSerwistRegistration = () => {
  const router = useRouter()

  useEffect(() => {
    if (!router.isReady) return
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return

    const inIframe = window.self !== window.top
    const path = cleanPath(router.asPath)
    const isBlocked = path.includes('/mm') || inIframe

    if (!isProd || isBlocked) {
      unregisterAllSW()
      return
    }

    registerSW().catch(err => {
      console.error('Failed to registerSW', err)
    })
  }, [router.isReady, router.asPath])
}
